import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { xosoService } from "@/services/xosoService";
import useXosoLive, { selectLiveResults } from "../XoSoCompound/useXosoLive";
import { LOTTERIES } from "./data";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const WEEKDAY_LONG = ["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];
const WEEKDAY_SHORT = ["CN", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];

const REGION_CODE = { mb: "MB", mn: "MN", mt: "MT" };
const DRAW_AT = { MB: "18:15", MN: "16:15", MT: "17:15" };

const PRIZE_KEYS = [
  ["specialPrize", "db"],
  ["prize1", "g1"],
  ["prize2", "g2"],
  ["prize3", "g3"],
  ["prize4", "g4"],
  ["prize5", "g5"],
  ["prize6", "g6"],
  ["prize7", "g7"],
  ["prize8", "g8"],
];

const NAME_GROUPS = [
  ["hochiminh", "tphcm", "tphochiminh", "thanhphohochiminh"],
  ["thuathienhue", "hue"],
];

const FETCHERS = {
  MB: (params) => xosoService.getXsmb(params),
  MN: (params) => xosoService.getXsmn(params),
  MT: (params) => xosoService.getXsmt(params),
};

function pad(value) {
  return String(value).padStart(2, "0");
}

export function vnToday() {
  const key = dayjs().tz(TZ).format("YYYY-MM-DD");
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function keyFromLocalDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function resolveDateKey(dateMode, picked) {
  const today = dayjs().tz(TZ).startOf("day");
  if (dateMode === "yesterday") return today.subtract(1, "day").format("YYYY-MM-DD");
  if (dateMode === "pick" && picked) return keyFromLocalDate(picked);
  return today.format("YYYY-MM-DD");
}

function drawMeta(dateKey) {
  const parsed = dayjs.utc(dateKey);
  const [year, month, day] = dateKey.split("-");
  return {
    weekdayLong: WEEKDAY_LONG[parsed.day()],
    weekdayShort: WEEKDAY_SHORT[parsed.day()],
    dash: `${day}-${month}-${year}`,
    slash: `${day}/${month}/${year}`,
  };
}

function pageTitle(draw, dateMode) {
  const prefix = dateMode === "today" ? "KQXS hôm nay" : "KQXS";
  return `${prefix} - Kết quả xổ số 3 miền ngày ${draw.slash}`;
}

function foldName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function sameStation(a, b) {
  const left = foldName(a);
  const right = foldName(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return NAME_GROUPS.some((group) => group.includes(left) && group.includes(right));
}

function displayName(apiName) {
  const hit = LOTTERIES.find((item) => item.kind === "province" && sameStation(item.name, apiName));
  return hit ? hit.name : apiName || "";
}

function toPrizes(prizes) {
  const out = {};
  PRIZE_KEYS.forEach(([from, to]) => {
    const list = prizes?.[from];
    out[to] = Array.isArray(list) ? list.map((value) => String(value)) : [];
  });
  return out;
}

function toStation(item) {
  const apiName = item?.province?.name || item?.provinceName || "";
  const key = item?.province?.code || item?.provinceCode || item?.id;
  if (!key) return null;
  return {
    key,
    name: displayName(apiName),
    apiName,
    prizes: toPrizes(item?.prizes),
  };
}

function toStations(rows) {
  const seen = new Set();
  const stations = [];
  (rows || []).forEach((item) => {
    const station = toStation(item);
    if (!station || seen.has(station.key)) return;
    seen.add(station.key);
    stations.push(station);
  });
  return stations;
}

function regionBlock(id, label, rows, draw) {
  return {
    id,
    kind: id === "mb" ? "mb" : "multi",
    name: label,
    codes: [],
    stations: toStations(rows),
    draw,
  };
}

function findRow(rows, lottery) {
  return (rows || []).find((item) => sameStation(item?.province?.name || item?.provinceName, lottery.name)) || null;
}

function liveRegions(lottery) {
  if (!lottery) return { MB: true, MN: true, MT: true };
  if (lottery.kind === "vietlott") return { MB: false, MN: false, MT: false };
  const code = REGION_CODE[lottery.region] || "MB";
  return { MB: code === "MB", MN: code === "MN", MT: code === "MT" };
}

function parseClock(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

function nowMinutes() {
  const now = dayjs().tz(TZ);
  return now.hour() * 60 + now.minute();
}

function reachedDraw(scheduledTime, region) {
  const cutoff = parseClock(scheduledTime) ?? parseClock(DRAW_AT[region]);
  return nowMinutes() >= cutoff;
}

function msUntilClock(clock) {
  const minutes = parseClock(clock);
  if (minutes == null) return null;
  const now = dayjs().tz(TZ);
  const target = now
    .hour(Math.floor(minutes / 60))
    .minute(minutes % 60)
    .second(0)
    .millisecond(0);
  const diff = target.diff(now);
  return diff > 0 ? diff : null;
}

function buildView(merged, lottery, draws, pageDraw, title, pending) {
  const hasRows = merged.MB.length + merged.MN.length + merged.MT.length > 0;
  if (pending && !hasRows) return { title, draw: pageDraw, blocks: [], empty: null };

  const blocksById = {
    mb: regionBlock("mb", "Miền Bắc", merged.MB, draws.MB),
    mn: regionBlock("mn", "Miền Nam", merged.MN, draws.MN),
    mt: regionBlock("mt", "Miền Trung", merged.MT, draws.MT),
  };

  if (!lottery) {
    const blocks = [blocksById.mb, blocksById.mn, blocksById.mt].filter((block) => block.stations.length);
    return { title, draw: pageDraw, blocks, empty: blocks.length ? null : "3 miền" };
  }

  if (lottery.kind === "vietlott") {
    return { title, draw: pageDraw, blocks: [], empty: lottery.name };
  }

  if (lottery.kind === "region") {
    const block = blocksById[lottery.region];
    if (!block?.stations.length) return { title, draw: block.draw, blocks: [], empty: lottery.name };
    return { title, draw: pageDraw, blocks: [block], empty: null };
  }

  const code = REGION_CODE[lottery.region];
  const row = code ? findRow(merged[code], lottery) : null;
  const station = row ? toStation(row) : null;
  if (!station) return { title, draw: draws[code] || pageDraw, blocks: [], empty: lottery.name };

  return {
    title,
    draw: pageDraw,
    blocks: [
      {
        id: `p-${station.key}`,
        kind: "province",
        name: station.name,
        stations: [station],
        draw: draws[code],
      },
    ],
    empty: null,
  };
}

const resultCache = {};

function cachedResults(region, date) {
  const rows = resultCache[`${region}|${date}`];
  return Array.isArray(rows) ? rows : null;
}

export default function useTruyenThongV3({ dateMode, picked, lottery }) {
  const dateKey = resolveDateKey(dateMode, picked);
  const todayKey = dayjs().tz(TZ).format("YYYY-MM-DD");
  const yesterdayKey = dayjs().tz(TZ).subtract(1, "day").format("YYYY-MM-DD");
  const watchingToday = dateMode === "today";
  const [clock, setClock] = useState(0);
  const [packVersion, setPackVersion] = useState(0);

  const liveOn = liveRegions(lottery);
  const liveMb = useXosoLive("MB", watchingToday && liveOn.MB);
  const liveMn = useXosoLive("MN", watchingToday && liveOn.MN);
  const liveMt = useXosoLive("MT", watchingToday && liveOn.MT);
  const scheduled = { MB: liveMb.scheduledTime, MN: liveMn.scheduledTime, MT: liveMt.scheduledTime };

  const opened = {
    MB: watchingToday && reachedDraw(scheduled.MB, "MB"),
    MN: watchingToday && reachedDraw(scheduled.MN, "MN"),
    MT: watchingToday && reachedDraw(scheduled.MT, "MT"),
  };
  const dates = {
    MB: watchingToday ? (opened.MB ? todayKey : yesterdayKey) : dateKey,
    MN: watchingToday ? (opened.MN ? todayKey : yesterdayKey) : dateKey,
    MT: watchingToday ? (opened.MT ? todayKey : yesterdayKey) : dateKey,
  };

  useEffect(() => {
    if (!watchingToday) return undefined;
    const waits = ["MB", "MN", "MT"].map((region) => msUntilClock(scheduled[region] || DRAW_AT[region]));
    const next = waits.filter((wait) => wait != null);
    if (!next.length) return undefined;
    const timer = setTimeout(() => setClock((value) => value + 1), Math.min(...next) + 200);
    return () => clearTimeout(timer);
  }, [watchingToday, scheduled.MB, scheduled.MN, scheduled.MT, clock]);

  useEffect(() => {
    let cancelled = false;
    const needed = ["MB", "MN", "MT"].filter((region) => !Array.isArray(resultCache[`${region}|${dates[region]}`]));
    if (!needed.length) {
      setPackVersion((value) => value + 1);
      return undefined;
    }

    (async () => {
      await Promise.all(
        needed.map(async (region) => {
          const date = dates[region];
          const rows = await FETCHERS[region]({ date });
          resultCache[`${region}|${date}`] = rows;
        })
      );
      if (!cancelled) setPackVersion((value) => value + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [dates.MB, dates.MN, dates.MT]);

  return useMemo(() => {
    const draws = {
      MB: drawMeta(dates.MB),
      MN: drawMeta(dates.MN),
      MT: drawMeta(dates.MT),
    };
    const shown = ["MB", "MN", "MT"].filter((region) => liveOn[region]);
    const anyOpen = shown.some((region) => opened[region]);
    const pageDate = watchingToday && shown.length ? (anyOpen ? todayKey : yesterdayKey) : dateKey;
    const title = pageTitle(drawMeta(dateKey), dateMode);
    const rest = {
      MB: cachedResults("MB", dates.MB) || [],
      MN: cachedResults("MN", dates.MN) || [],
      MT: cachedResults("MT", dates.MT) || [],
    };
    const pending = shown.some((region) => !cachedResults(region, dates[region]));
    const merged = {
      MB: opened.MB ? selectLiveResults(rest.MB, liveMb) : rest.MB,
      MN: opened.MN ? selectLiveResults(rest.MN, liveMn) : rest.MN,
      MT: opened.MT ? selectLiveResults(rest.MT, liveMt) : rest.MT,
    };
    return buildView(merged, lottery, draws, drawMeta(pageDate), title, pending);
  }, [
    clock,
    dateKey,
    dateMode,
    dates.MB,
    dates.MN,
    dates.MT,
    liveMb,
    liveMn,
    liveMt,
    liveOn,
    lottery,
    opened.MB,
    opened.MN,
    opened.MT,
    packVersion,
    todayKey,
    watchingToday,
    yesterdayKey,
  ]);
}
